// client/src/components/Navbar.js
import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Badge,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { AuthContext } from '../context/AuthContext';
import { CartContext } from '../context/CartContext';
import { FeatureFlagContext } from '../context/FeatureFlagContext';
import { useThemeMode } from '../theme/ThemeModeContext';
import { useStore } from '../context/StoreContext';
import { getImageUrl } from '../utils/media';
import SearchAutocomplete from './SearchAutocomplete';
import MiniCartDrawer from './MiniCartDrawer';
import CategoryBar from './CategoryBar';
import NotificationBell from './NotificationBell';
import GoogleTranslateWidget from './GoogleTranslateWidget';

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const { cartItems } = useContext(CartContext);
  const { isEnabled } = useContext(FeatureFlagContext);
  const { mode, toggleMode } = useThemeMode();
  const { store } = useStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);

  const cartItemCount = cartItems.reduce((count, item) => count + item.quantity, 0);

  const closeUserMenu = () => setUserMenuAnchor(null);
  const handleLogout = () => {
    closeUserMenu();
    logout();
    navigate('/');
  };

  const showSellerLink = isEnabled('become_seller') && (!user || user.role === 'customer');

  // Desktop: full text buttons next to the logo. Live Auctions lives in the
  // CategoryBar row below instead.
  const navLinks = (
    <>
      {showSellerLink && (
        <Button component={Link} to="/become-seller">
          Sell on {store.name}
        </Button>
      )}
    </>
  );

  // Mobile: same destinations, collapsed to icon buttons in the toolbar
  // (there's no hamburger drawer on phones any more).
  const mobileNavIcons = (
    <>
      {showSellerLink && (
        <Tooltip title={`Sell on ${store.name}`}>
          <IconButton component={Link} to="/become-seller" aria-label={`Sell on ${store.name}`}>
            <StorefrontIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  );

  return (
    <AppBar position="sticky" color="inherit" elevation={1} className="no-print">
      <Toolbar sx={{ gap: 2, py: 1, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Box
          component={Link}
          to="/"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none', flexShrink: 0 }}
        >
          {store.logo_url && (
            <Box
              component="img"
              src={getImageUrl(store.logo_url)}
              alt=""
              sx={{ height: 36, width: 36, objectFit: 'contain', borderRadius: 1 }}
            />
          )}
          <Typography
            variant="h5"
            sx={{ color: 'primary.main', fontWeight: 800 }}
          >
            {store.name}
          </Typography>
        </Box>

        {!isMobile && <Box sx={{ display: 'flex', gap: 1 }}>{navLinks}</Box>}

        <SearchAutocomplete sx={{ flexGrow: 1, order: { xs: 3, md: 0 }, width: { xs: '100%', md: 'auto' } }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', order: { xs: 2, md: 0 } }}>
          {isMobile && mobileNavIcons}

          <GoogleTranslateWidget sx={{ mr: 0.5 }} />

          <IconButton onClick={toggleMode} aria-label="Toggle dark mode">
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>

          {user && isEnabled('wishlist') && (
            <IconButton component={Link} to="/profile" aria-label="Wishlist">
              <FavoriteBorderIcon />
            </IconButton>
          )}

          {user && <NotificationBell />}

          <IconButton component={Link} to="/compare" aria-label="Compare products">
            <CompareArrowsIcon />
          </IconButton>

          <IconButton onClick={() => setCartOpen(true)} aria-label="Shopping cart">
            <Badge badgeContent={cartItemCount} color="secondary">
              <ShoppingCartIcon />
            </Badge>
          </IconButton>

          {user ? (
            <>
              <IconButton onClick={(e) => setUserMenuAnchor(e.currentTarget)} sx={{ ml: 0.5 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.9rem' }}>
                  {user.email?.[0]?.toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={closeUserMenu}>
                <MenuItem disabled sx={{ opacity: '1 !important', fontWeight: 600 }}>{user.email}</MenuItem>
                <Divider />
                <MenuItem component={Link} to="/profile" onClick={closeUserMenu}>My Account</MenuItem>
                <MenuItem onClick={handleLogout}>Logout</MenuItem>
              </Menu>
            </>
          ) : (
            <Button component={Link} to="/login" variant="contained" sx={{ ml: 1 }}>
              Login
            </Button>
          )}
        </Box>
      </Toolbar>

      <CategoryBar />
      <MiniCartDrawer open={cartOpen} onClose={() => setCartOpen(false)} />
    </AppBar>
  );
};

export default Navbar;
